import React, { Fragment, VFC } from 'react';
import {
  Container,
  Row,
  Col,
  Visible,
  useScreenClass,
} from 'react-grid-system';
import styled from 'styled-components';
import Spacing from '../components/atoms/Spacing';
import Typography from '../components/atoms/Typography';
import TableItem from '../components/molecules/TableItem';
import PrimaryButton from '../components/molecules/PrimaryButton';
import LoadingSpinner from '../components/molecules/LoadingSpinner';
import SvgIcon from '../components/atoms/SvgIcon';

import { useSettings } from '../contexts/settingsContext';
import { useReturnContext } from '../contexts/returnContext';
import { theme } from '../utils/theme';
import Banner from '../components/molecules/Banner';

interface Props {
  radius?: string;
}

const Border = styled(Container)<Props>`
  border-radius: ${({ radius }) => radius};
  border: 1px solid #e0e0e0;
`;

const Item = styled(Row)<Props>`
  padding: 32px;
  border-radius: ${({ radius }) => radius};
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
`;

const Section = styled(Row)`
  border-bottom: 1px solid #e0e0e0;
  padding: 32px 0px;
`;

const SectionGrey = styled(Row)`
  padding: 32px;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
`;

const FreightImage = styled.img`
  max-width: 32px;
  object-fit: contain;
  aspect-ratio: 3/4;
`;

const Loader = () => (
  <>
    <Spacing spacing={2} />
    <Row justify="center">
      <LoadingSpinner color={theme.colors.grey[80]} />
    </Row>
    <Spacing spacing={2} />
    <Row justify="center">
      <Typography translateKey="processing_return" variant="h4" />
    </Row>
    <Spacing spacing={2} />
  </>
);

const ReturnCompletePage: VFC = () => {
  const { cornerRadius } = useSettings();
  const { selectedCarrier, loading, returnResponse, instructions } =
    useReturnContext();

  const screenClass = useScreenClass();

  const downloadLabel = () => {
    window.open(returnResponse.data.returnLabelUrl);
  };

  const table = [
    {
      key: 'carrier',
      title: <Typography variant="body1-semi-bold" translateKey="carrier" />,
      value: selectedCarrier.carrier,
    },
    {
      key: 'carrier_service',
      title: (
        <Typography variant="body1-semi-bold" translateKey="carrier_service" />
      ),
      value: selectedCarrier.service,
    },
    {
      key: 'no_of_parcels',
      title: (
        <Typography variant="body1-semi-bold" translateKey="no_of_parcels" />
      ),
      value: selectedCarrier.numberOfParcels,
    },
  ];
  return (
    <Border radius={cornerRadius}>
      {loading ? (
        <Loader />
      ) : (
        <Col>
          <Section justify="center">
            <Col>
              <Row justify="center">
                <SvgIcon icon="check-with-circle" color="#4CAF50" iconSize="xLarge" />
              </Row>
              <Spacing spacing={2} />
              <Row justify="center">
                <Typography variant="h3" translateKey="return_registered" />
              </Row>
              <Spacing spacing={2} />
              <Row justify="center">
                <Typography
                  variant="body1-semi-bold"
                  translateKey="follow_instructions"
                />
              </Row>
            </Col>
          </Section>
          {returnResponse && returnResponse.status === 'Success' && (
            <SectionGrey>
              <Col>
                <Item radius={cornerRadius}>
                  <Col xs={12} sm={12} md={12} lg="content">
                    <Row justify="start">
                      <FreightImage src={selectedCarrier.logoUrl} />
                    </Row>
                  </Col>

                  {table?.map((item) => (
                    <Col key={item.key} xs={12} sm={12} md={12} lg="content">
                      <Row
                        nogutter={!!['lg', 'xl', 'xxl'].includes(screenClass)}
                      >
                        <TableItem title={item.title} value={item.value} />
                      </Row>

                      <Visible xs sm md>
                        <Spacing spacing={2} />
                      </Visible>
                    </Col>
                  ))}
                  <Col>
                    <Row justify="end">
                      {returnResponse?.data?.returnLabelUrl && (
                        <PrimaryButton
                          onClick={downloadLabel}
                          icon="download"
                          translateKey="download_labels"
                        />
                      )}
                    </Row>
                  </Col>
                </Item>
              </Col>
            </SectionGrey>
          )}
          {instructions && instructions.length > 0 && (
            <Row justify="center">
              <Col>
                <Spacing spacing={4} />
                <Row justify="center">
                  <Typography variant="h3" translateKey="instructions" />
                </Row>
                <Spacing spacing={2} />
                <Row align="center" justify="center">
                  <Col>
                    {instructions?.map((step) => (
                      <Fragment key={step.instruction}>
                        <Row justify="center">
                          <Typography variant="body2">{`${step.step}. `}</Typography>
                          <Spacing horizontal />
                          <Typography variant="body2">
                            {step.instruction}
                          </Typography>
                        </Row>
                        <Spacing />
                      </Fragment>
                    ))}
                  </Col>
                </Row>
                <Spacing spacing={4} />
              </Col>
            </Row>
          )}
        </Col>
      )}

      {returnResponse && returnResponse.status === 'PartialSuccess' && (
        <>
          <Spacing spacing={3} />
          <Banner message="customer_service_message" />
          <Spacing spacing={3} />
        </>
      )}
    </Border>
  );
};

export default ReturnCompletePage;
